// Web Push, client half: ask permission, register this device with the server, and remember the
// choice so the app never nags.
//
// Platform reality this file has to live with: on iOS/iPadOS, push works ONLY when the app has been
// added to the Home Screen and opened from there. In a Safari tab the APIs exist but subscribing
// fails. So we detect that case and say so, rather than showing a dead toggle.
import { api } from './api.ts';
import type { PushConfig } from '@alignova/shared';

export type PushState =
  | 'unsupported' // no service worker / no Push API at all
  | 'needs-install' // iOS Safari tab: must be added to the Home Screen first
  | 'disabled-on-server' // no VAPID keys configured
  | 'denied' // the user said no; only they can undo it, in browser settings
  | 'off' // supported, permitted or not asked yet, not subscribed
  | 'on';

const isIos = (): boolean =>
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  // iPadOS 13+ reports as a Mac; the touch points give it away.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** True when the page is running as an installed app rather than a browser tab. */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as { standalone?: boolean }).standalone === true;

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** urlBase64 → Uint8Array, the format PushManager.subscribe expects for the VAPID key. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalised);
  // Allocate the buffer explicitly: PushManager wants an ArrayBuffer-backed view, and a bare
  // Uint8Array is typed as possibly SharedArrayBuffer-backed.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export const pushConfig = (): Promise<PushConfig> => api.get<PushConfig>('/push/config');

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Where this device stands right now. Cheap enough to call on every settings render. */
export async function pushState(config?: PushConfig): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  const cfg = config ?? (await pushConfig().catch(() => ({ publicKey: null, devices: 0 })));
  if (!cfg.publicKey) return 'disabled-on-server';
  if (Notification.permission === 'denied') return 'denied';
  return (await currentSubscription()) ? 'on' : 'off';
}

/**
 * Turn notifications on for this device. Must be called from a user gesture — browsers refuse a
 * permission prompt that the user did not ask for, and rightly so.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  const cfg = await pushConfig();
  if (!cfg.publicKey) return 'disabled-on-server';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true, // we never push silently; the platform requires this anyway
      applicationServerKey: urlBase64ToUint8Array(cfg.publicKey),
    }));

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return 'off';
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent.slice(0, 300),
  });
  return 'on';
}

/** Turn them off for THIS device only; other devices keep theirs. */
export async function disablePush(): Promise<PushState> {
  const sub = await currentSubscription();
  if (sub) {
    await api.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return 'off';
}

export const sendTestPush = (): Promise<{ ok: boolean; sent: number }> =>
  api.post<{ ok: boolean; sent: number }>('/push/test', {});
